import type { CheckoutFaq } from 'dpl/payments/components/checkout/CheckoutHelpSection';
import { PAYMENT_REQUEST_CATEGORIES } from 'dpl/shared/constants/breeder_app';

// The fields the Edit payment sheet drafts and saves back together.
export interface EditablePaymentFields {
  amount_in_dollars: number | string;
  category: string;
  personal_message: string;
  litter_id: number | null;
  dog_id: number | null;
  _pickupLocationValue: string | null;
  pickup_location_attributes: Record<string, any> | null;
}

export const EDITABLE_FIELDS = [
  'amount_in_dollars',
  'category',
  'personal_message',
  'litter_id',
  'dog_id',
  '_pickupLocationValue',
  'pickup_location_attributes'
] as const satisfies readonly (keyof EditablePaymentFields)[];

// The sentence says what this payment reserves, whatever the breeder's
// deposit_secures says, in the same order as the card's terms line.
export function getNextStepText({
  category,
  breederFirstName,
  dogName,
  litterId,
  finalPaymentTiming
}: {
  category: string;
  breederFirstName: string;
  dogName?: string | null;
  litterId?: number | null;
  finalPaymentTiming: string | null;
}) {
  if (category !== PAYMENT_REQUEST_CATEGORIES.DEPOSIT) {
    return `${breederFirstName} reviews and confirms your payment, and you both get a confirmation.`;
  }

  if (dogName) {
    const reminder = finalPaymentTiming
      ? " We'll remind you before the balance is due."
      : '';

    return `${dogName} is reserved for you, and you and ${breederFirstName} both get a confirmation.${reminder}`;
  }

  // A spot isn't a puppy yet, so these say what happens before the pick.
  if (litterId) {
    return `your spot is saved. ${breederFirstName} will reach out when it's your turn to pick.`;
  }

  return "your spot is saved. We'll let you know when the litter arrives and it's time to pick.";
}

export function getCheckoutFaqs({
  isDogPayment,
  klarnaEnabled
}: {
  isDogPayment: boolean;
  klarnaEnabled: boolean;
}): CheckoutFaq[] {
  return [
    {
      question: 'What payment methods can I use?',
      answer:
        'You can pay by credit or debit card, Apple Pay, Google Pay, or bank transfer. Credit card payments have a 3.3% processing fee.'
    },
    klarnaEnabled && {
      question: 'Can I pay over time?',
      answer:
        'Yes! With Klarna, you can split your payment into four interest-free installments on eligible purchases under $2,000 or choose monthly financing.'
    },
    isDogPayment && {
      question: 'Why do you need pick up location?',
      answer:
        "We use your puppy's pickup location to calculate sales tax. Sales tax applies to the full purchase price."
    },
    {
      question: 'Is my payment protected?',
      answer:
        "Payments made through Good Dog are secure and covered by Good Dog's Payment Protection."
    },
    {
      question: 'What if I need a refund?',
      answer:
        "Refunds are subject to your breeder's policy. Please reach out to your breeder for more information."
    }
  ].filter(Boolean) as CheckoutFaq[];
}
